import { useEffect, useRef, useState } from 'react'
import {
  GeoJSONSource, LngLatBounds, Map as MapLibreMap, MapMouseEvent, NavigationControl, Popup, addProtocol, setWorkerUrl,
  type LayerSpecification, type StyleSpecification,
} from 'maplibre-gl'
import { Protocol } from 'pmtiles'

import 'maplibre-gl/dist/maplibre-gl.css'

// El worker (y el módulo que él importa) se publican con `scripts/copy-maplibre-worker.mjs`.
// Sin esto MapLibre lo pide a una ruta que no existe, y las capas de datos nunca cargan.
setWorkerUrl(`${import.meta.env.BASE_URL}maplibre/maplibre-gl-worker.mjs`)
// El mapa propio es un solo archivo PMTiles que se lee por rangos (mapas-servidor.sh). Una vez por
// página: la registra quien importe este módulo, también la portada de ingreso.
addProtocol('pmtiles', new Protocol().tile)
import type { Alert, AssignmentView, Incident, UnitView, MapArea, MapBase } from './api'
import { DEFAULT_MAP_BASE } from './api'
import { translate, type Lang } from './i18n'
import { groupByPlace, type Bounds } from './queue'

/**
 * Mapa en vivo (§10.1): dónde está cada unidad y a qué punto debería estar yendo.
 *
 * Mapa base OpenStreetMap, aceptable para desarrollo y demos de bajo volumen (§16.2). Producción
 * necesita tiles propios o un proveedor con SLA: se cambia en [style] sin tocar el resto.
 */
export interface AlertPoint {
  alertId: string
  patrolUnitId: string
  callSign: string
  severity: 'Warning' | 'Critical'
  latitude: number
  longitude: number
}

export function LiveMap({
  units,
  assignments,
  alertPoints,
  alerts,
  dark,
  lang,
  selected,
  selectedAlert,
  onSelect,
  onSelectAlert,
  onBounds,
  base = DEFAULT_MAP_BASE,
  area = null,
  zones = null,
  background,
  incidents = [],
  selectedIncident = null,
  onSelectIncident = () => {},
}: {
  units: UnitView[]
  assignments: AssignmentView[]
  alertPoints: AlertPoint[]
  alerts: Alert[]
  dark: boolean
  lang: Lang
  selected: string | null
  selectedAlert: string | null
  onSelect: (patrolUnitId: string | null) => void
  onSelectAlert: (alertId: string | null) => void
  /** Lo que se está mirando, para poder acotar la cola a esa zona. */
  onBounds: (bounds: Bounds) => void
  /** El mapa base configurado (§16.2). */
  base?: MapBase
  /** El recuadro de la agencia, donde abre el mapa. */
  area?: MapArea | null
  /** Las zonas de patrullaje con su forma, en GeoJSON (§10.1). */
  zones?: GeoJSON.FeatureCollection | null
  /** El fondo de la agencia para el mapa oscuro (§16.3d); sin él, el de la página. */
  background?: string
  /**
   * Los incidentes que reportaron las unidades. Se ven los abiertos; uno cerrado, solo si se elige
   * (para ver dónde fue). Antes no estaban en el mapa y elegirlos en la lista no hacía nada.
   */
  incidents?: Incident[]
  selectedIncident?: string | null
  onSelectIncident?: (incidentId: string | null) => void
}) {
  const container = useRef<HTMLDivElement>(null)
  const map = useRef<MapLibreMap | null>(null)
  const fitted = useRef(false)
  // Sube cada vez que el mapa queda listo para recibir capas y datos. Es estado, no una bandera,
  // para que el dibujo se repita en cuanto eso pasa.
  const [ready, setReady] = useState(0)
  const [broken, setBroken] = useState<string | null>(null)
  // El mapa registra sus oyentes una sola vez: con la referencia, siempre llaman al manejador vigente.
  const onSelectIncidentRef = useRef(onSelectIncident)
  onSelectIncidentRef.current = onSelectIncident

  useEffect(() => {
    if (!container.current || map.current) return   // ya está creado: un montaje repetido no crea otro
    const instance = new MapLibreMap({
      container: container.current,
      style: baseStyle(dark, base, background),
      ...opening(area),
      attributionControl: { compact: true },
    })
    instance.addControl(new NavigationControl({ showCompass: false }), 'top-right')
    const report = () => {
      const view = instance.getBounds()
      onBounds({ west: view.getWest(), south: view.getSouth(), east: view.getEast(), north: view.getNorth() })
    }
    instance.on('moveend', report)
    instance.on('load', report)
    instance.on('load', () => {
      setBroken(addLayers(instance))
      setReady((version) => version + 1)
    })
    instance.on('click', 'units', (e: MapMouseEvent & { features?: GeoJSON.Feature[] }) => {
      const id: unknown = e.features?.[0]?.properties?.patrolUnitId
      onSelect(typeof id === 'string' ? id : null)
    })
    // Tocar una alerta en el mapa la elige en la cola, y al revés: son la misma cosa vista de dos formas.
    instance.on('click', 'alerts', (e: MapMouseEvent & { features?: GeoJSON.Feature[] }) => {
      const id: unknown = e.features?.[0]?.properties?.alertId
      onSelectAlert(typeof id === 'string' ? id : null)
    })
    instance.on('click', 'incidents', (e: MapMouseEvent & { features?: GeoJSON.Feature[] }) => {
      const id: unknown = e.features?.[0]?.properties?.incidentId
      onSelectIncidentRef.current(typeof id === 'string' ? id : null)
    })
    instance.on('click', (e: MapMouseEvent) => {
      if (instance.queryRenderedFeatures(e.point, { layers: ['units', 'alerts', 'incidents'] }).length === 0) {
        onSelect(null)
        onSelectAlert(null)
        onSelectIncidentRef.current(null)
      }
    })
    for (const layer of ['units', 'alerts']) {
      instance.on('mouseenter', layer, () => { instance.getCanvas().style.cursor = 'pointer' })
      instance.on('mouseleave', layer, () => { instance.getCanvas().style.cursor = '' })
    }
    // Quién es cada punto, al pasar por encima. Sin capa de etiquetas: eso exige un servicio de
    // glifos externo, y el mapa tiene que funcionar sin él.
    const hint = new Popup({ closeButton: false, closeOnClick: false, offset: 12 })
    for (const layer of ['units', 'alerts']) {
      instance.on('mousemove', layer, (e: MapMouseEvent & { features?: GeoJSON.Feature[] }) => {
        const properties = e.features?.[0]?.properties
        if (!properties) return
        const count = Number(properties.count ?? 1)
        const text = count > 1
          ? `${properties.callSign}: ${translate(lang, 'map.stack', [count])}`
          : String(properties.callSign ?? '')
        hint.setLngLat(e.lngLat).setText(text).addTo(instance)
      })
      instance.on('mouseleave', layer, () => hint.remove())
    }
    map.current = instance
    // Para inspeccionar el mapa desde la consola: siempre en desarrollo, y en cualquier despliegue
    // abriendo la página con ?debug=1 (no cambia nada de lo que se ve).
    if (import.meta.env.DEV || new URLSearchParams(location.search).has('debug')) {
      (window as unknown as { map?: MapLibreMap }).map = instance
    }

    // El mapa se crea una sola vez y se destruye solo cuando su elemento sale de la página.
    //
    // React en desarrollo monta, desmonta y vuelve a montar cada componente para detectar efectos
    // mal escritos. Destruir el mapa en ese desmontaje simulado deja a la instancia siguiente
    // esperando para siempre a que cargue el estilo (comparten el mismo grupo de workers), y el
    // mapa queda sin capas: sin unidades ni puntos asignados.
    const element = container.current
    return () => {
      queueMicrotask(() => {
        if (document.body.contains(element)) return
        instance.remove()
        map.current = null
      })
    }
    // El estilo se cambia aparte; este efecto solo crea el mapa una vez.
  }, [])

  // El mapa ocupa lo que le deje la pantalla: al cambiar el tamaño del panel (o la ventana, o una
  // pantalla de sala de mando), el lienzo se reajusta.
  useEffect(() => {
    const element = container.current
    if (!element) return
    const observer = new ResizeObserver(() => map.current?.resize())
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  // El mapa sigue el tema de la aplicación (§16.1). Cambiar el estilo borra las capas: se vuelven
  // a poner y el dibujo se repite solo, porque `ready` cambia.
  useEffect(() => {
    const instance = map.current
    if (!instance || ready === 0) return
    // Sin diferencia: del oscuro al claro hay que quitar la inversión de los mosaicos, no ajustarla.
    instance.setStyle(baseStyle(dark, base, background), { diff: false })
    instance.once('styledata', () => {
      setBroken(addLayers(instance))
      setReady((version) => version + 1)
    })
  }, [dark, base?.styleUrl, base?.darkStyleUrl, base?.tileUrl])

  // El fondo de la agencia llega después de abrir el mapa: se repinta solo esa capa, sin rehacer
  // el estilo ni las capas de datos.
  useEffect(() => {
    const instance = map.current
    if (!instance || ready === 0 || !background || !instance.getLayer('fondo')) return
    instance.setPaintProperty('fondo', 'background-color', background)
  }, [background, ready])

  // Elegir un incidente lleva el mapa a donde ocurrió.
  useEffect(() => {
    const instance = map.current
    const i = incidents.find((x) => x.incidentId === selectedIncident)
    if (!instance || !i || i.latitude === null || i.longitude === null) return
    instance.easeTo({ center: [i.longitude, i.latitude], zoom: Math.max(instance.getZoom(), 15), duration: 600 })
  }, [selectedIncident, incidents])

  // Elegir una alerta lleva el mapa a donde ocurrió.
  useEffect(() => {
    const instance = map.current
    const point = alertPoints.find((p) => p.alertId === selectedAlert)
    if (!instance || !point) return
    instance.easeTo({ center: [point.longitude, point.latitude], zoom: Math.max(instance.getZoom(), 14), duration: 600 })
  }, [selectedAlert, alertPoints])

  useEffect(() => {
    const instance = map.current
    if (!instance || ready === 0) return
    paint(instance, units, assignments, alertPoints, alerts, selected, selectedAlert)
    paintIncidents(instance, incidents, selectedIncident)
    void instance.getSource<GeoJSONSource>('zones')?.setData(zones ?? { type: 'FeatureCollection', features: [] })
    // El primer encuadre muestra toda la agencia; después la vista es del operador.
    if (!fitted.current) fitted.current = fit(instance, units, assignments)
  }, [ready, units, assignments, alertPoints, alerts, selected, selectedAlert, zones, incidents, selectedIncident])

  // Elegir una unidad la centra: es lo que el operador quiere ver al tocarla en la lista.
  useEffect(() => {
    const instance = map.current
    const unit = units.find((u) => u.patrolUnitId === selected)
    if (!instance || !unit || unit.latitude === null || unit.longitude === null) return
    instance.easeTo({ center: [unit.longitude, unit.latitude], zoom: Math.max(instance.getZoom(), 14), duration: 600 })
  }, [selected, units])

  return (
    <div className="map">
      <div ref={container} style={{ position: 'absolute', inset: 0 }} />
      {broken && <div className="map-error">{translate(lang, 'map.broken')}: {broken}</div>}
      <div className="legend">
        <div className="row"><span className="dot" style={{ background: '#1f5fa8' }} /> {translate(lang, 'map.legendUnit')}</div>
        <div className="row"><span className="dot" style={{ background: '#9a6400' }} /> {translate(lang, 'map.legendTarget')}</div>
        <div className="row"><span className="dot" style={{ background: '#e8a33d' }} /> {translate(lang, 'map.legendWarning')}</div>
        <div className="row"><span className="dot" style={{ background: '#b3261e' }} /> {translate(lang, 'map.legendCritical')}</div>
        <div className="row"><span className="dot incident" /> {translate(lang, 'map.legendIncident')}</div>
      </div>
    </div>
  )
}

/**
 * Las capas de datos, como especificación.
 *
 * Es un valor y no una llamada al mapa para poder validarlo en una prueba: una expresión mal
 * escrita hace que `addLayer` falle, y entonces no se dibuja **nada** (ni unidades, ni puntos, ni
 * alertas). Eso ya pasó una vez con `['case', ['get', x], …]`, que exige un booleano y recibe un
 * valor cualquiera.
 */
export const DataLayers: LayerSpecification[] = [
  // Las zonas de patrullaje van primero, debajo de todo (§10.1): son el fondo contra el que se lee
  // una alerta «fuera de zona». Relleno tenue y borde, para no tapar unidades ni alertas.
  {
    id: 'zones-fill',
    type: 'fill',
    source: 'zones',
    paint: { 'fill-color': '#3b6ea8', 'fill-opacity': 0.08 },
  },
  {
    id: 'zones-line',
    type: 'line',
    source: 'zones',
    paint: { 'line-color': '#3b6ea8', 'line-width': 1.5, 'line-opacity': 0.7, 'line-dasharray': [2, 1] },
  },
  {
    id: 'targets',
    type: 'circle',
    source: 'targets',
    paint: {
      'circle-radius': 6,
      'circle-color': '#9a6400',
      'circle-stroke-width': 2,
      'circle-stroke-color': '#ffffff',
      'circle-opacity': 0.85,
    },
  },
  // Las alertas van debajo de las unidades: un anillo alrededor del lugar donde el plan falló.
  // El anillo exterior solo aparece cuando hay más de una alerta en el mismo punto.
  {
    id: 'alert-stacks',
    type: 'circle',
    source: 'alerts',
    filter: ['>', ['get', 'count'], 1],
    paint: {
      'circle-radius': ['case', ['==', ['get', 'selected'], true], 30, 21],
      'circle-color': 'rgba(0, 0, 0, 0)',
      'circle-stroke-width': 2,
      'circle-stroke-color': ['case', ['==', ['get', 'severity'], 'Critical'], '#b3261e', '#e8a33d'],
      'circle-stroke-opacity': 0.8,
    },
  },
  {
    id: 'alerts',
    type: 'circle',
    source: 'alerts',
    paint: {
      'circle-radius': ['case', ['==', ['get', 'selected'], true], 24, 15],
      'circle-color': ['case', ['==', ['get', 'severity'], 'Critical'], '#b3261e', '#e8a33d'],
      'circle-opacity': 0.35,
      'circle-stroke-width': ['case', ['==', ['get', 'selected'], true], 4, 2],
      'circle-stroke-color': ['case', ['==', ['get', 'severity'], 'Critical'], '#b3261e', '#e8a33d'],
    },
  },
  // El incidente: punto rojo lleno con borde blanco grueso, distinto del anillo translúcido de una
  // alerta. Uno ya cerrado (solo se ve si se elige) va en gris: fue ahí, pero ya no está pasando.
  {
    id: 'incidents',
    type: 'circle',
    source: 'incidents',
    paint: {
      'circle-radius': ['case', ['==', ['get', 'selected'], true], 13, 9],
      'circle-color': ['case', ['==', ['get', 'open'], true], '#b3261e', '#6b7280'],
      'circle-stroke-width': ['case', ['==', ['get', 'selected'], true], 4, 3],
      'circle-stroke-color': '#ffffff',
    },
  },
  {
    id: 'units',
    type: 'circle',
    source: 'units',
    paint: {
      'circle-radius': ['case', ['==', ['get', 'selected'], true], 12, 8],
      'circle-color': ['case', ['==', ['get', 'alerted'], true], '#b3261e', '#1f5fa8'],
      'circle-stroke-width': 3,
      'circle-stroke-color': ['case', ['==', ['get', 'offline'], true], '#9a6400', '#ffffff'],
    },
  },
]

/**
 * Devuelve el problema si alguna capa no se pudo poner.
 *
 * Importa que se vea: sin capas el mapa base se sigue dibujando, así que un error aquí pasa
 * desapercibido y el operador cree que no hay nada que mostrar cuando en realidad no se le está
 * mostrando nada.
 */
function addLayers(map: MapLibreMap): string | null {
  const empty: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] }
  try {
    for (const id of ['zones', 'targets', 'alerts', 'incidents', 'units']) {
      if (!map.getSource(id)) map.addSource(id, { type: 'geojson', data: empty })
    }
    for (const layer of DataLayers) {
      if (!map.getLayer(layer.id)) map.addLayer(layer)
    }
    return null
  } catch (e) {
    return e instanceof Error ? e.message : String(e)
  }
}

function paint(
  map: MapLibreMap,
  units: UnitView[],
  assignments: AssignmentView[],
  alertPoints: AlertPoint[],
  alerts: Alert[],
  selected: string | null,
  selectedAlert: string | null,
) {
  const alerted = new Set(alerts.filter((a) => a.status !== 'Resolved' && a.status !== 'Closed').map((a) => a.patrolUnitId))
  const unitSource = map.getSource<GeoJSONSource>('units')
  const targetSource = map.getSource<GeoJSONSource>('targets')
  if (!unitSource || !targetSource) return

  void unitSource.setData({
    type: 'FeatureCollection',
    features: units
      .filter((u) => u.latitude !== null && u.longitude !== null)
      .map((u) => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [u.longitude as number, u.latitude as number] },
        properties: {
          patrolUnitId: u.patrolUnitId,
          callSign: u.callSign,
          alerted: alerted.has(u.patrolUnitId),
          offline: u.connectivity === 'Offline' || u.connectivity === 'Never',
          selected: u.patrolUnitId === selected,
        },
      })),
  })

  // Una marca por punto: dos visitas falladas al mismo hotspot no pueden verse como una sola.
  const alertSource = map.getSource<GeoJSONSource>('alerts')
  void alertSource?.setData({
    type: 'FeatureCollection',
    features: [...groupByPlace(alertPoints).values()].map((group) => {
      const first = group[0]!
      return {
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [first.longitude, first.latitude] },
        properties: {
          alertId: first.alertId,
          count: group.length,
          severity: group.some((p) => p.severity === 'Critical') ? 'Critical' : 'Warning',
          callSign: [...new Set(group.map((p) => p.callSign))].join(', '),
          selected: group.some((p) => p.alertId === selectedAlert),
        },
      }
    }),
  })

  void targetSource.setData({
    type: 'FeatureCollection',
    features: assignments
      .filter((a) => a.status !== 'Completed' && (selected === null || a.patrolUnitId === selected))
      .map((a) => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [a.longitude, a.latitude] },
        properties: { label: a.label, callSign: a.callSign },
      })),
  })
}

/** Los incidentes abiertos, y el elegido aunque esté cerrado. Sin ubicación no hay nada que dibujar. */
const ABIERTOS = new Set(['Reported', 'Validated', 'Active', 'Controlled'])

function paintIncidents(map: MapLibreMap, incidents: Incident[], selectedIncident: string | null) {
  void map.getSource<GeoJSONSource>('incidents')?.setData({
    type: 'FeatureCollection',
    features: incidents
      .filter((i) => i.latitude !== null && i.longitude !== null)
      .filter((i) => ABIERTOS.has(i.status) || i.incidentId === selectedIncident)
      .map((i) => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [i.longitude as number, i.latitude as number] },
        properties: {
          incidentId: i.incidentId, callSign: i.callSign,
          open: ABIERTOS.has(i.status), selected: i.incidentId === selectedIncident,
        },
      })),
  })
}

function fit(map: MapLibreMap, units: UnitView[], assignments: AssignmentView[]): boolean {
  const points: [number, number][] = [
    ...units.filter((u) => u.latitude !== null).map((u) => [u.longitude as number, u.latitude as number] as [number, number]),
    ...assignments.map((a) => [a.longitude, a.latitude] as [number, number]),
  ]
  if (points.length === 0) return false
  const bounds = points.reduce((b, p) => b.extend(p), new LngLatBounds(points[0], points[0]))
  map.fitBounds(bounds, { padding: 60, maxZoom: 14, duration: 0 })
  return true
}

/**
 * Dónde abre un mapa: el recuadro de la agencia si se conoce; si no, el mundo entero.
 *
 * Antes abría siempre sobre Fort Worth, así que una agencia de otra ciudad veía Texas hasta que
 * llegaban sus datos, y si no tenía datos todavía, Texas se quedaba. El mundo entero no es útil,
 * pero no le afirma a nadie una ubicación que no es la suya.
 */
export function opening(area: MapArea | null | undefined) {
  return area
    ? { bounds: [[area.west, area.south], [area.east, area.north]] as [[number, number], [number, number]] }
    : { center: [0, 20] as [number, number], zoom: 1.5 }
}

/** El fondo de la aplicación ahora mismo: el de la agencia en el tema oscuro (§16.3d). */
function currentBg(): string {
  const v = typeof document === 'undefined' ? '' : getComputedStyle(document.documentElement).getPropertyValue('--bg').trim()
  return /^#[0-9A-Fa-f]{6}$/.test(v) ? v : '#0A1628'
}

/**
 * El mapa base que toca: el estilo vectorial del despliegue si lo hay (el oscuro en tema oscuro) y,
 * si no, los mosaicos raster de {@link style}.
 */
export function baseStyle(dark: boolean, base: MapBase = DEFAULT_MAP_BASE, fondo?: string): StyleSpecification | string {
  if (base.styleUrl) return (dark ? base.darkStyleUrl : null) ?? base.styleUrl
  return style(dark, base, fondo)
}

/**
 * Estilo con mosaicos raster del proveedor configurado (§16.2). En modo oscuro no se cambia de
 * proveedor: el mosaico se invierte y se pone sobre el fondo de la agencia, calles claras sobre su
 * azul (o su color), como en los diseños del Centro de Mando.
 */
export function style(dark: boolean, base: MapBase = DEFAULT_MAP_BASE, fondo?: string): StyleSpecification {
  return {
    version: 8,
    sources: {
      osm: {
        type: 'raster',
        tiles: [base.tileUrl],
        tileSize: 256,
        maxzoom: base.maxZoom,
        attribution: base.attribution,
      },
    },
    layers: dark
      ? [
          { id: 'fondo', type: 'background', paint: { 'background-color': fondo ?? currentBg() } },
          {
            id: 'osm',
            type: 'raster',
            source: 'osm',
            // Invertido y sin color, con el terreno gris oscuro y no negro: con 60 % de opacidad y el
            // brillo máximo en 0.05 el mapa se veía casi negro y las calles no se distinguían (igual
            // que en el teléfono, H-106).
            paint: {
              'raster-saturation': -1, 'raster-brightness-min': 0.95, 'raster-brightness-max': 0.3,
              'raster-contrast': 0.3, 'raster-opacity': 1,
            },
          },
        ]
      : [{ id: 'osm', type: 'raster', source: 'osm', paint: {} }],
  }
}
